import React, { memo, useEffect, useRef, useState } from "react";
import { animated, config, interpolate, useSpring } from "react-spring";

interface ParallaxProps {
  sensitivity?: number;
  disabled?: boolean;
}

export const Parallax: React.FC<ParallaxProps> = memo((props) => {
  const layer = useRef<HTMLDivElement>();
  const [shown, setShown] = useState(false);

  const [parallaxTransition, setY] = useSpring(() => ({
    y: 0,
    config: {
      mass: 1,
      tension: 1000,
      friction: 70,
    },
  }));

  const enterTransition = useSpring({
    opacity: shown ? 1 : 0,
    y: shown ? 0 : 100,
    config: config.default,
  });

  const transform = interpolate(
    [parallaxTransition.y, enterTransition.y],
    (parallax, enter) => {
      return `translate3d(0, ${parallax + enter}px, 0)`;
    }
  );

  const filter = enterTransition.opacity.interpolate(
    (opacity) => `saturate(${opacity}) opacity(${opacity}) `
  );

  useEffect(() => {
    const tick = () => {
      const layerRect = layer.current.getBoundingClientRect();

      const viewportPosition =
        (layer.current.scrollTop - layerRect.top + window.innerHeight) /
        (layer.current.clientHeight + window.innerHeight);

      const offset = viewportPosition - 0.5;
      const scaled = offset * -100;
      const sensitivity = props.sensitivity || 1;

      if (!props.disabled) {
        setY({ y: scaled * sensitivity });
      }

      if (!shown) {
        if (viewportPosition > 0.0 && viewportPosition < 1.0) {
          setShown(true);
        }
      }
    };

    tick();

    window.addEventListener("scroll", tick, { passive: true });

    return () => window.removeEventListener("scroll", tick);
  }, [props.disabled, props.sensitivity, setY, shown]);

  useEffect(() => {
    setY({
      y: 0,
    });
  }, [props.disabled, setY]);

  return (
    <div ref={layer}>
      <animated.div
        style={{
          transform,
          filter,
        }}
      >
        {props.children}
      </animated.div>
    </div>
  );
});
